
import { useContentStore } from "@/stores/content";
import { generateStlInWorker } from '@/lib/stl-generator/workerClient'
import { defineComponent, onMounted, ref, onUnmounted, watch } from "vue";
import STLPreviewVue from "./STLPreview.vue";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { SlicerService } from '@/services/slicerService'
import { DownloadService } from '@/services/downloadService'
import { AIPredictionService } from '@/services/aiPredictionService'
interface AIPredictionProgress {
  stage: 'loading' | 'processing' | 'predicting' | 'completed' | 'error'
  message: string
  progress?: number
}
import { STLLoadingState } from '@/library/other'
import { buildRequestBody } from '@/library/utilities/payloadBuilder'
import {VoronoiCompensationRegistry} from "@/services/voronoiCompensationRegistry";
import { LocalCompRegistry } from '@/library/localCompRegistry';

export default defineComponent({
  components: { STLPreviewVue },
  setup() {
    const contentStore = useContentStore();
    const precisionIdx = ref(1);
    const precisionValue = ["Low", "Medium", "High"];
    const precisionCompIdx   = ref(1)
    const precisionCompValue = ["Low", "Medium", "High"] as const
    const globalChecked = ref(false);
    const localChecked = ref(false);
    const aiChecked = ref(false);
    const globalCompensation = ref(100);

    const localCompensationMin = ref(100);
    const localCompensationMax = ref(800);
    const minAt = ref(200);
    const maxAt = ref(4000);

    const offsetChecked = ref(false)
    // AI-Offset 四个输入框（默认与 Local 相同）
    const offsetMin   = ref(10)
    const offsetMinAt = ref(200)
    const offsetMax   = ref(50)
    const offsetMaxAt = ref(4000)

    watch(aiChecked,  (v) => { if (v) localChecked.value = false })
    watch(localChecked, (v) => { if (v) aiChecked.value  = false })


    const showStlPreview = ref(false);
    const isBinary = ref(true);
    const isExportingStep = ref(false);
    const internalOnly = ref(false);


    // AI-related reactive variables
    const aiModelLoaded = ref(false);           // Whether AI model is loaded
    const aiProcessing = ref(false);            // Whether AI is processing
    const aiProgress = ref<AIPredictionProgress | null>(null); // AI processing progress
    const aiError = ref<string | null>(null);  // AI error information


// Generate STEP only when requested so it does not block the STL preview.
  async function onExportSTEP() {
  if (isExportingStep.value) return;
  isExportingStep.value = true;

  try {
    await new Promise(resolve => setTimeout(resolve, 50));
    console.log('[STEP] Starting on-demand STEP generation...')
    const stepData = await contentStore.requestStepData()
    const url = URL.createObjectURL(stepData)
    const a = document.createElement('a')
    a.href = url
    a.download = contentStore.title ? `${contentStore.title}.step` : 'fluid3d.step'
    a.click()
    console.log(`[STEP] Stage 6/6 complete: browser download triggered for ${a.download}`)
    setTimeout(() => URL.revokeObjectURL(url), 0)
  } catch (e) {
    if ((e as Error).name === 'AbortError') return
    console.error('[STEP] export failed:', e)
    alert(`STEP export failed: ${(e as Error).message}`)
  } finally {
    isExportingStep.value = false;
  }
}



    onUnmounted(() => {
      if (aiModelLoaded.value) {
        AIPredictionService.cleanup()
      }
    })

    const loadSTL = async () => {
     
      try {
        // 1. set the loading state and display the modal
        contentStore.stlLoadingState = STLLoadingState.Loading;
        contentStore.hasAIExtraFiles = false;
        showStlPreview.value = true;

        // 2. AI initialization
        if (aiChecked.value) {
          const aiInitSuccess = await initializeAIModel()
          if (!aiInitSuccess) {
            aiChecked.value = false
          }
        }

        // 3. give UI some time to update
        await new Promise(resolve => setTimeout(resolve, 100));


        const startTime = performance.now();
        await contentStore.requestNewStlData(
            precisionValue[precisionIdx.value],
            globalChecked.value,
            globalCompensation.value,

            // localCheck = Local or (AI  && offsetChecked)
            localChecked.value || (aiChecked.value && offsetChecked.value),

            localChecked.value ? localCompensationMin.value
                : (aiChecked.value && offsetChecked.value ? offsetMin.value : 0),
            localChecked.value ? minAt.value
                : (aiChecked.value && offsetChecked.value ? offsetMinAt.value : 0),
            localChecked.value ? localCompensationMax.value
                : (aiChecked.value && offsetChecked.value ? offsetMax.value : 0),
            localChecked.value ? maxAt.value
                : (aiChecked.value && offsetChecked.value ? offsetMaxAt.value : 0),

            isBinary.value,
            undefined,
            internalOnly.value
        )
        const endTime = performance.now();
        console.log(`[Performance] Standard STL generation time: ${(endTime - startTime).toFixed(2)} ms`);


        if (aiChecked.value && aiModelLoaded.value) {
          //LocalCompRegistry.clear();
          contentStore.isAIProcessing = true;
          contentStore.stlLoadingState = STLLoadingState.Loading;

          const aiStartTime = performance.now(); // 新增：AI总计时开始


          let stlDataForSlicing: ArrayBuffer

          if (aiChecked.value) {
            // ① ingore offsetcheck. If AI -> must use the "original STL" to slice
            const rawJsonStr = buildRequestBody(
                precisionValue[precisionIdx.value],
                globalChecked.value,
                globalCompensation.value,
                /* localCheck = false */ false,
                0, 0, 0, 0
            )
            const rawJson = JSON.parse(rawJsonStr)

            //  ASCII STL for slicing (un-scaled, keep in um)
            stlDataForSlicing = await generateStlInWorker(rawJson, undefined, false, false, internalOnly.value)
          } else {
            // ② not AI
            if (contentStore.userSelectedBinaryFormat) {
              // Binary → require ASCII
              const asciiJsonStr = buildRequestBody(
                  precisionValue[precisionIdx.value],
                  globalChecked.value,
                  globalCompensation.value,
                  localChecked.value,
                  localCompensationMin.value,
                  minAt.value,
                  localCompensationMax.value,
                  maxAt.value
              )
              const asciiJson = JSON.parse(asciiJsonStr)
              stlDataForSlicing = await generateStlInWorker(asciiJson, undefined, false, false, internalOnly.value)
            } else {
              stlDataForSlicing = contentStore.stlData
            }
          }

          const sliceStartTime = performance.now();
          await SlicerService.generateSlices(stlDataForSlicing, 100.5, 10)
          const sliceEndTime = performance.now();
          console.log(`[Performance] Total slice generation time: ${(sliceEndTime - sliceStartTime).toFixed(2)} ms`);



          const availableElevations = SlicerService.getAvailableElevations();

          setTimeout(async () => {
            try {
              await performAIPrediction();
              if (contentStore.stlLoadingState === STLLoadingState.Loading) {
                contentStore.stlLoadingState = STLLoadingState.Succeed;
              }
            } catch (err: any) {
              contentStore.stlLoadingState = STLLoadingState.Fail;
              aiError.value = err.message || 'Unknown error occurred';
              console.error(err);
            } finally {
              contentStore.hasAIExtraFiles = true;
              contentStore.isAIProcessing = false;
              const aiEndTime = performance.now(); //
              console.log(`[Performance] Total time for AI-compensated STL generation: ${(aiEndTime - aiStartTime).toFixed(2)} ms`);
            }
          }, 100);
        }
        // Non-AI case: requestNewStlData has been set to Succeed, and STL is displayed directly

      } catch (error: any) {
        if (error?.name === 'AbortError') return
        console.error('[UI] Process failed:', error);
        contentStore.stlLoadingState = STLLoadingState.Fail;
        alert(`Generation failed: ${error.message}`);
      }
    }

    const initializeAIModel = async () => {
      if (aiModelLoaded.value) {
        console.log('[UI] AI model already loaded, skipping initialization')
        return true
      }

      try {
        aiError.value = null
        const modelLoadStartTime = performance.now();
        await AIPredictionService.initialize((progress) => {
          aiProgress.value = progress
        })

        const modelLoadEndTime = performance.now();
        console.log(`[Performance] Initial model loading time: ${(modelLoadEndTime - modelLoadStartTime).toFixed(2)} ms`);

        aiModelLoaded.value = true
        return true

      } catch (error: any) {
        aiError.value = `AI model loading failed: ${error.message}`
        aiModelLoaded.value = false
        return false
      }
    }

    const performAIPrediction = async () => {
      if (!aiChecked.value) {
        console.log('[UI] AI not checked, skipping prediction')
        return
      }

      try {
        aiProcessing.value = true
        aiError.value = null

        // 1. construction chipJSON 这里是基础Json，因为localChecked.value,肯定是false
        const chipJSONString = buildRequestBody(
            precisionValue[precisionIdx.value],
            globalChecked.value,
            globalCompensation.value,
            localChecked.value,
            localCompensationMin.value,
            minAt.value,
            localCompensationMax.value,
            maxAt.value
        )
        const chipJSON = JSON.parse(chipJSONString)

        // 2. execute AI prediction
        const predictions = await AIPredictionService.performPrediction(
            (progress) => { aiProgress.value = progress },
            chipJSON,
            precisionCompValue[precisionCompIdx.value],
            globalChecked.value,
            globalCompensation.value,
            offsetChecked.value,          // localCheck = Offset
            offsetMin.value,
            offsetMinAt.value,
            offsetMax.value,
            offsetMaxAt.value,
            internalOnly.value
        )



      } catch (error: any) {
        aiError.value = `AI prediction failed: ${error.message}`
        alert(`AI prediction failed: ${error.message}`)
        throw error;
      } finally {
        aiProcessing.value = false
        aiProgress.value = null
      }
    }

    // execute slicing
    const performSlicing = async () => {
      try {

        const stlData = contentStore.stlData
        if (!stlData || stlData.byteLength === 0) {
          throw new Error('empty STL data')
        }

        const result = await SlicerService.generateSlices(
            stlData,
            100.5,
            10
        )

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
        const zipFilename = `${contentStore.title}_slices_${timestamp}.zip`

        await DownloadService.downloadSlicesAsZip(
            result.sliceImages,
            result.sliceInfo,
            zipFilename
        )


      } catch (error:any) {
        alert('Slicing failed: ' + error.message)
      }
    }


    const downloadSVGZip = () => {
      const zip = new JSZip();
      const color = "#0d6efd";
      const svgTag = `<svg width="${contentStore.chipLengthX / 10}" height="${
        contentStore.chipLengthY / 10
      }" viewBox="-5 -5 ${contentStore.chipLengthX + 5} ${
        contentStore.chipLengthY + 5
      }" xmlns="http://www.w3.org/2000/svg" fill="${color}" stroke="${color}">`;

      const chipBoundary = `<rect x="0" y="0" width="${contentStore.chipLengthX}" height="${contentStore.chipLengthY}" fill="none" stroke-width="10" stroke="black"/>`;
      try {
        const svgContent = document.getElementById("svgContent");
        svgContent?.childNodes.forEach((layerContent: any) => {
          const layer = contentStore.layers.get(layerContent.id);
          if (layer) {
            const svgData = `${svgTag}${chipBoundary}${layerContent.innerHTML}</svg>`;
            zip.file(`${layer.label}_${layer.height}.svg`, svgData);
          }
        });
        zip.generateAsync({ type: "blob" }).then(function (content: Blob) {
          saveAs(content, `${contentStore.title}_svg.zip`);
        });
      } catch (err) {
        console.log(err);
      }
    };

    onMounted(() => {
      const myModalEl = document.getElementById("outputOptionModal");
      if (myModalEl) {  // null check
        myModalEl.addEventListener("hidden.bs.modal", () => {
          showStlPreview.value = false;
        });
      }
    });


    return {
      globalChecked,
      localChecked,
      aiChecked,
      performSlicing,
      globalCompensation,
      localCompensationMin,
      localCompensationMax,
      minAt,
      maxAt,
      showStlPreview,
      loadSTL,
      isBinary,
      precisionIdx,
      precisionValue,
      precisionCompIdx,
      precisionCompValue,
      downloadSVGZip,
      aiModelLoaded,
      aiProcessing,
      aiProgress,
      aiError,
      initializeAIModel,
      performAIPrediction,
      offsetMin, offsetMinAt, offsetMax, offsetMaxAt,
      offsetChecked,
      internalOnly,
      onExportSTEP,
      isExportingStep
    };
  }
});
